import { useState } from 'react';
import { AGREEMENT_DOCUMENT_VERSION, agreementVersionAtLeast, AGREEMENT_V5_DOCUMENT_VERSION, missingAgreementRequirements, withAgreementLanguage, withClientAiConsent, withClientEsignConsent, withClientLiabilityAck, withoutRemoteClientSignature, withSignatures, type AgreementLanguage, type V2Agreement, type V2AgreementParty } from '../../domain/agreement';
import { signatureSvg } from '../../domain/signature';
import { displayAddress } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { labelWithTag } from '../../i18n/labels';
import { Button, CheckRow, Icon, RowInput, SheetShell, SignaturePad } from '../../ui/components';
import { formatDate } from '../../ui/hooks';
import type { AgreementSubject } from './AgreementCard';
import { AgreementLanguageRow } from './AgreementLanguageRow';
import { AgreementViewSheet } from './AgreementViewSheet';

// 签署协议 (Round 2 §C): both parties on one sheet — typed name (defaults from the inspection),
// hand-drawn signature, "read and agree" confirmation — then one 完成签署. Cancel discards the
// draft (OC §9.4). The document itself can be read from here before signing.
//
// Owner-selected design 2026-09-14 (Figma 191:10 "AGREEMENT · B · Contract Formal"): above the two
// signature blocks a contract excerpt — the facts of THIS inspection (property · client · inspector ·
// date · fee, the same fields the agreement PDF prints) — which is also the 先阅读协议 entry. Each
// block: heading with 清除 at the right, the name row, the pad, the tick sentence. The footer names
// what is still missing, or 双方已完成签署, then the AI notice, 完成签署 and 取消.
// Copy of 2026-10-19 (Owner Q2 / Q3): the CLIENT ticks, in the client's block, the consent to electronic
// records and signatures (required to sign here) and the consent to cloud storage + AI-assisted analysis
// (voluntary). Each tick is timed when it is given; the agreement PDF prints the time.
export function AgreementSignSheet(props: { agreement: V2Agreement; subject: AgreementSubject; onSave: (next: V2Agreement) => void; onClose: () => void }) {
  const { t, locale } = useT();
  // The document as this sheet holds it: the Client's remote part can be dropped here (back to in person).
  const [base, setBase] = useState<V2Agreement>(props.agreement);
  const [client, setClient] = useState<V2AgreementParty>({ ...props.agreement.client, name: props.agreement.client.name || props.subject.client.name });
  const [inspector, setInspector] = useState<V2AgreementParty>({ ...props.agreement.inspector, name: props.agreement.inspector.name || props.subject.inspector.name });
  const [esign, setEsign] = useState<{ on: boolean; at: number | null }>({ on: props.agreement.clientEsignConsent, at: props.agreement.clientEsignConsentAt });
  const [aiConsent, setAiConsent] = useState<{ on: boolean; at: number | null }>({ on: props.agreement.clientAiConsent, at: props.agreement.clientAiConsentAt });
  // v5 (Owner 2026-10-28): the Client's tick on the limitation of liability and claims terms, required.
  const [liability, setLiability] = useState<{ on: boolean; at: number | null }>({ on: props.agreement.clientLiabilityAck, at: props.agreement.clientLiabilityAckAt });
  const asksLiability = agreementVersionAtLeast(AGREEMENT_DOCUMENT_VERSION, AGREEMENT_V5_DOCUMENT_VERSION);
  const [viewOpen, setViewOpen] = useState(false);
  // Owner items 4 / 45: the Client already signed through a one-time link — their block is what they gave.
  const remoteClient = base.clientRemote ? base.clientRemote : null;
  // The ticks are applied to the document before the signatures, so what is signed carries them.
  const ticked = withClientLiabilityAck(withClientAiConsent(withClientEsignConsent(base, esign.on, esign.at), aiConsent.on, aiConsent.at), liability.on, liability.at);

  function signInPersonInstead() {
    const next = withoutRemoteClientSignature(base);
    setBase(next);
    setClient({ ...next.client, name: next.client.name || props.subject.client.name });
    setEsign({ on: false, at: null });
    setAiConsent({ on: false, at: null });
    setLiability({ on: false, at: null });
  }

  // v5 (Owner 2026-10-28): the language of the document, chosen here with the client. What was drawn or
  // ticked was given for the document in the other language, so it starts again (a link's part included).
  function changeLanguage(language: AgreementLanguage) {
    if (language === base.documentLanguage) return;
    setBase(withAgreementLanguage(base, language));
    setClient({ ...client, signature: null, accepted: false, signedAt: null });
    setInspector({ ...inspector, signature: null, accepted: false, signedAt: null });
    setEsign({ on: false, at: null });
    setAiConsent({ on: false, at: null });
    setLiability({ on: false, at: null });
  }
  const preview = withSignatures(ticked, client, inspector, Date.now());
  const all = missingAgreementRequirements(preview);
  const missing = all.filter((m) => m !== 'fee' && m !== 'additionalServices');
  const feeMissing = all.includes('fee');
  const servicesIncomplete = all.includes('additionalServices');
  const canComplete = missing.length === 0 && !feeMissing && !servicesIncomplete;
  const notSet = t('common.notSet');
  const fee = base.demo ? t('agreement.feeDemo') : base.fee.amount.trim().length > 0 ? `$${base.fee.amount.trim()}` : notSet;
  const formatDateTime = (ts: number) => new Date(ts).toLocaleString(locale, { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  // Owner-frozen AI notice (2026-09-13 §30 A): in sight where the document is signed. Since the Android
  // acceptance of 2026-10-21 (issue 6) it stands right under the AI consent it is about, in the client's
  // block, instead of between the signing status and 完成签署 where it read like a legal warning.
  const aiNotice = (
    <p className="ai-disclaimer sign-consents-disclaimer" data-testid="sign-ai-disclaimer">
      {t('ai.disclaimer')}
    </p>
  );
  const facts: Array<[string, string, string]> = [
    [t('agreement.docProperty'), displayAddress({ property: props.subject.property }, t('inspection.noAddress')), 'property'],
    [t('agreement.docClient'), client.name.trim() || notSet, 'client'],
    [t('agreement.docInspector'), inspector.name.trim() || notSet, 'inspector'],
    [t('agreement.docInspectionDate'), formatDate(props.subject.inspectionAt), 'date'],
    [t('agreement.docFee'), fee, 'fee']
  ];

  function remoteClientBlock(remote: NonNullable<V2Agreement['clientRemote']>) {
    return (
      <section className="sign-party sign-party-remote" data-testid="sign-client-remote">
        <div className="sign-party-head">
          <h3 className="sign-party-title">{t('agreement.clientSignature')}</h3>
          <button type="button" className="link-inline" onClick={signInPersonInstead} data-testid="sign-client-in-person">
            {t('remote.inPersonInstead')}
          </button>
        </div>
        <p className="sign-remote-name">{client.name}</p>
        {client.signature ? <span className="sign-remote-mark" dangerouslySetInnerHTML={{ __html: signatureSvg(client.signature, { width: 240, height: Math.round((240 * client.signature.height) / client.signature.width) }) }} /> : null}
        <p className="card-note" data-testid="sign-client-remote-at">
          {t('remote.signedAt', { date: formatDateTime(remote.receivedAt) })}
        </p>
        <p className="card-note">{esign.on && esign.at !== null ? t('remote.esignGiven', { date: formatDateTime(esign.at) }) : null}</p>
        {asksLiability ? (
          <p className="card-note" data-testid="sign-client-remote-liability">
            {liability.on && liability.at !== null ? t('remote.liabilityGiven', { date: formatDateTime(liability.at) }) : t('remote.liabilityMissing')}
          </p>
        ) : null}
        <p className="card-note">{aiConsent.on && aiConsent.at !== null ? t('remote.aiGiven', { date: formatDateTime(aiConsent.at) }) : t('remote.aiNotGiven')}</p>
        {aiNotice}
      </section>
    );
  }

  function party(label: string, value: V2AgreementParty, set: (next: V2AgreementParty) => void, acceptLabel: string, key: 'client' | 'inspector') {
    if (key === 'client' && remoteClient) return remoteClientBlock(remoteClient);
    const hasStrokes = value.signature !== null && value.signature.strokes.length > 0;
    return (
      <section className="sign-party" data-testid={`sign-${key}`}>
        <div className="sign-party-head">
          <h3 className="sign-party-title">{label}</h3>
          <button type="button" className="link-inline" onClick={() => set({ ...value, signature: null })} disabled={!hasStrokes} data-testid={`sign-${key}-clear`}>
            {t('agreement.clear')}
          </button>
        </div>
        <RowInput id={`sign-${key}-name`} label={labelWithTag(locale, t('agreement.signerName'), t('common.required'))} value={value.name} onChange={(name) => set({ ...value, name })} placeholder={t('agreement.signerNamePlaceholder')} testId={`sign-${key}-name`} />
        <SignaturePad value={value.signature} onChange={(signature) => set({ ...value, signature })} clearLabel={t('agreement.clear')} hint={t('agreement.drawHere')} showClear={false} testId={`sign-${key}-pad`} />
        <CheckRow label={acceptLabel} checked={value.accepted} onChange={(accepted) => set({ ...value, accepted })} testId={`sign-${key}-accept`} />
        {key === 'client' ? (
          <div className="sign-consents" data-testid="sign-client-consents">
            <CheckRow label={labelWithTag(locale, t('agreement.esignTick'), t('common.required'))} checked={esign.on} onChange={(on) => setEsign({ on, at: on ? Date.now() : null })} testId="sign-client-esign" />
            {asksLiability ? <CheckRow label={labelWithTag(locale, t('agreement.liabilityTick'), t('common.required'))} checked={liability.on} onChange={(on) => setLiability({ on, at: on ? Date.now() : null })} testId="sign-client-liability" /> : null}
            <CheckRow label={labelWithTag(locale, t('agreement.aiTick'), t('common.optional'))} checked={aiConsent.on} onChange={(on) => setAiConsent({ on, at: on ? Date.now() : null })} testId="sign-client-ai-consent" />
            {aiNotice}
            <p className="card-note sign-consents-note">{t('agreement.consentsNote')}</p>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <SheetShell
      full
      title={t('agreement.signTitle')}
      subtitle={t('agreement.signBody')}
      onClose={props.onClose}
      layer="over"
      testId="agreement-sign-sheet"
      footer={
        <div className="sign-footer">
          {!canComplete ? (
            <p className="blockers-note sign-footer-note" data-testid="sign-missing">
              {feeMissing ? t('agreement.req.feeFirst') : servicesIncomplete ? t('agreement.req.servicesFirst') : t('agreement.missing', { list: missing.map((m) => t(`agreement.req.${m}` as const)).join(' · ') })}
            </p>
          ) : (
            <p className="sign-footer-note sign-footer-done" data-testid="sign-complete">
              {t('agreement.signedBoth')}
            </p>
          )}
          <Button icon="draw" onClick={() => props.onSave(withSignatures(ticked, client, inspector, Date.now()))} disabled={!canComplete} testId="agreement-complete">
            {t('agreement.completeSigning')}
          </Button>
          <Button variant="text" onClick={props.onClose} testId="agreement-sign-cancel">
            {t('shell.cancel')}
          </Button>
        </div>
      }
    >
      <AgreementLanguageRow agreement={preview} onChange={changeLanguage} testId="sign-language" />
      <button type="button" className="agreement-excerpt" onClick={() => setViewOpen(true)} data-testid="sign-read-agreement">
        <span className="agreement-excerpt-title">
          <Icon name="description" size={20} />
          {t('agreement.readBeforeSigning')}
        </span>
        <span className="agreement-excerpt-rows" data-testid="sign-facts">
          {facts.map(([key, value, id]) => (
            <span className="agreement-excerpt-row" key={key} data-testid={`sign-fact-${id}`}>
              <span className="agreement-excerpt-key">{key}</span>
              <span className="agreement-excerpt-value">{value}</span>
            </span>
          ))}
        </span>
      </button>
      {party(t('agreement.clientSignature'), client, setClient, t('agreement.acceptClient'), 'client')}
      {party(t('agreement.inspectorSignature'), inspector, setInspector, t('agreement.acceptInspector'), 'inspector')}
      {viewOpen ? <AgreementViewSheet agreement={preview} subject={props.subject} onClose={() => setViewOpen(false)} layer="over" /> : null}
    </SheetShell>
  );
}
